import { useEffect, useMemo, useRef, useState } from "react";
import { AppTokenPayload, OutputEmail, OutputEmailSuggestion, SignatureRequest, Workflow, WorkflowAutomationItem, WorkflowOutput, WorkflowOutputSuggestion } from "./interface";
import { jwtDecode } from "jwt-decode";
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon, MailIcon, PaperClipIcon, SparklesIcon } from "@heroicons/react/outline";
import { MIMETYPE_ICON_MAP, BLOCK_ICON_MAP } from "./utils";
import { postData } from "./backend";
import { showToast } from "./toast";
import Breadcrumbs from "./Breadcrumbs";
import { useTranslation } from "react-i18next";
import PDFViewer from "./PDFViewer";
import OutputEmailEditor, { OutputEmailEditorHandle } from "./OutputEmailEditor";
import AiSuggestionSidebar from "./AiSuggestionSidebar";
import DocumentSuggestionsSidebar from "./DocumentSuggestionsSidebar";
import BetaLabel from "./BetaLabel";
import SignatureRequestEmailPreview from "./SignatureRequestEmailPreview";
import ApproverStatusPill, { ApprovalDisplayStatus } from "./ApproverStatusPill";
import Avatar from "./Avatar";
import OutputEmailStatusIndicator from "./OutputEmailStatusIndicator";
import SignatureRequestStatusIndicator from "./SignatureRequestStatusIndicator";
import TruncateTooltip from "./TruncateTooltip";
import {
  getDocumentCount,
  getPrimaryCtaLabel,
  getPrimaryCtaKey,
  getRelevantUnseen,
  getSuccessBodyKey,
  getSuccessTitleKey,
  getUnseenConfirmKey,
  getActiveSignatureRequests,
  groupOutputEmailsByAutoSend,
  getManualSendNote,
  isOutputEmailAutoSend,
  isOutputEmailSent,
  getSignatureRequestRecipientLabel,
  hasRelevantUnseen,
  isSignatureManualSendEnabled,
  isSoloReview,
  getUnseenConfirmMessage,
  getUnseenFooterWarning,
  workflowHasSignatureRequests,
} from "./reviewCtaUtils";
import { closeHubSpotIframeModal } from "./hubspotModal";

interface Props {
  token: string;
  workflow?: Workflow;
  automationItem?: WorkflowAutomationItem;
  initialOutputIndex?: number;
}

type ReviewState = "PENDING" | "APPROVING" | "APPROVED" | "REJECTING" | "REJECTED" | "SENDING" | "SENT";
type Tab = "DOCUMENTS" | "SIGNATURES" | "EMAILS";
type EmailAiPhase = "generating" | "ready" | "actioning";
type DocumentAiPhase = "generating" | "ready";
type DocumentAiMode = { outputId: string; phase: DocumentAiPhase };

function mapApproverRowStatus(apiStatus: string): ApprovalDisplayStatus {
  if (apiStatus === "APPROVED" || apiStatus === "REJECTED")
    return apiStatus;
  if (apiStatus === "REQUESTED")
    return "REVIEWING";
  return "WAITING";
}

function countRecipients(email: OutputEmail): number {
  const all = [email.emailTo, email.emailCc, email.emailBcc]
    .filter(Boolean)
    .flatMap(s => s.split(/[,;]/))
    .map(s => s.trim())
    .filter(Boolean);
  return all.length;
}

function getRecipientSummary(email: OutputEmail, t: (s: string) => string): string {
  const count = countRecipients(email);
  if (count === 0) return t("No recipients");
  if (count === 1) return email.emailTo.split(/[,;]/)[0].trim() || t("1 recipient");
  return `${count} ${t("recipients")}`;
}

function getOutputFileIcon(output: WorkflowOutput): string {
  const mime = output.file?.mimeType ?? output.filePdf?.mimeType ?? output.onedriveFile?.fileMimeType ?? "application/pdf";
  return MIMETYPE_ICON_MAP[mime] ?? MIMETYPE_ICON_MAP["application/pdf"];
}

function getOutputDisplayName(output: WorkflowOutput): string {
  return output.file?.name ?? output.onedriveFile?.fileName ?? output.filePdf?.name ?? output.outputName ?? "Untitled";
}

function AppWorkflowPreview(props: Props) {
  const { t } = useTranslation();

  const { token, workflow } = props;

  const [reviewState, setReviewState] = useState<ReviewState>("PENDING");
  const [showRejectPanel, setShowRejectPanel] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [rejectMessage, setRejectMessage] = useState("");
  const [sentEmailIds, setSentEmailIds] = useState<Set<string>>(() => new Set());
  const emailEditorRefs = useRef<Map<string, OutputEmailEditorHandle | null>>(new Map());

  const [outputs, setOutputs] = useState<WorkflowOutput[]>(props.automationItem?.outputs || []);
  const [outputEmails, setOutputEmails] = useState<OutputEmail[]>(props.automationItem?.outputEmails || []);
  const [emailAiPersonalization, setEmailAiPersonalization] = useState<{
    emailId: string;
    phase: EmailAiPhase;
    suggestion?: OutputEmailSuggestion;
  } | null>(null);
  const [documentAiMode, setDocumentAiMode] = useState<DocumentAiMode | null>(null);
  const documentSuggestionInFlightOutputIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setOutputs(props.automationItem?.outputs || []);
    setOutputEmails(props.automationItem?.outputEmails || []);
    setEmailAiPersonalization(null);
    setDocumentAiMode(null);
    documentSuggestionInFlightOutputIdsRef.current.clear();
  }, [props.automationItem]);

  useEffect(() => {
    setSentEmailIds(new Set());
  }, [props.automationItem?.id]);

  const [activeTab, setActiveTab] = useState<Tab>("DOCUMENTS");
  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);
  const [currentSignatureIndex, setCurrentSignatureIndex] = useState(0);
  const [currentEmailIndex, setCurrentEmailIndex] = useState(0);
  const [showPDFVersion, setShowPDFVersion] = useState(false);
  // Scoped to an output id so a stale hash is never applied to another document.
  const [navigation, setNavigation] = useState<{ outputId: string; key: string } | undefined>();

  const appliedInitialOutputRef = useRef<string | null>(null);
  useEffect(() => {
    if (props.initialOutputIndex === undefined || outputs.length === 0) return;
    const key = `${props.automationItem?.id ?? ""}:${props.initialOutputIndex}`;
    if (appliedInitialOutputRef.current === key) return;
    if (props.initialOutputIndex >= 0 && props.initialOutputIndex < outputs.length) {
      setCurrentOutputIndex(props.initialOutputIndex);
      setActiveTab("DOCUMENTS");
    }
    appliedInitialOutputRef.current = key;
  }, [props.initialOutputIndex, props.automationItem?.id, outputs]);

  const currentOutput = outputs[currentOutputIndex];
  const handleSuggestionsChange = (outputId: string, suggestions: WorkflowOutputSuggestion[]) => {
    setOutputs(prev => prev.map(o => o.id === outputId ? { ...o, suggestions } : o));
  };
  const currentEmail = outputEmails[currentEmailIndex];

  const googleFile = currentOutput?.file;
  const microsoftFile = currentOutput?.onedriveFile;
  const hubspotFile = currentOutput?.hubspotFilePdf;
  const previewUrl = currentOutput?.previewUrl;

  // Slides
  const isGoogleSlides = !!googleFile?.url?.includes("/presentation/");
  const navigationKey = navigation?.outputId === currentOutput?.id ? navigation?.key : undefined;
  // const slideTarget = navigationKey
  //   ? (navigationKey.startsWith("id.") ? navigationKey : `id.${navigationKey}`)
  //   : "";
  const encodedNavigationKey = navigationKey
    ? encodeURIComponent(navigationKey)
    : "";
  const googleNavigationSuffix = navigationKey
    ? (isGoogleSlides ? `#slide=${encodedNavigationKey}` : `#heading=${encodedNavigationKey}`)
    : "";
  const hasEditableSource = !!(googleFile?.url || microsoftFile?.fileEmbedViewUrl);
  const showPdfToggle = hasEditableSource && !!previewUrl;

  const isReviewPending = props.automationItem?.stage === "REVIEW_PENDING";
  const canReview = isReviewPending && !!props.automationItem?.approvableByUser;

  // Docs 
  const googleBaseUrl = googleFile?.url
    ? googleFile.url.split(/[?#]/)[0].replace("/edit", canReview ? "/edit" : "/preview").replace("/view", "/preview")
    : undefined;
  const googleQuery = `rm=minimal${isGoogleSlides ? "" : "&tab=t.0"}`;
  const googleEmbedUrl = googleBaseUrl
    ? (googleNavigationSuffix
      ? `${googleBaseUrl}?${googleQuery}${googleNavigationSuffix}`
      : `${googleBaseUrl}?widget&${googleQuery}`)
    : undefined;

  const appTokenPayload = useMemo(() => {
    try {
      return jwtDecode<AppTokenPayload>(token);
    } catch {
      return undefined;
    }
  }, [token]);

  const currentUserId = appTokenPayload?.user_id != null ? String(appTokenPayload.user_id) : undefined;
  const userSuggestionsEnabled = !!appTokenPayload?.user_suggestions_enabled;

  function isEditableOutputEmail(email: OutputEmail | undefined): boolean {
    return !!email
      && !isOutputEmailSent(email, sentEmailIds)
      && String(email.sender?.id ?? "") === String(currentUserId ?? "");
  }

  const isEmailAiMode = emailAiPersonalization !== null;
  const isDocumentAiMode = documentAiMode !== null;
  const isAnyAiMode = isEmailAiMode || isDocumentAiMode;
  const aiEmailId = emailAiPersonalization?.emailId;
  const aiEmail = aiEmailId ? outputEmails.find(e => e.id === aiEmailId) : undefined;
  const showPersonalizeButton = userSuggestionsEnabled
    && activeTab === "EMAILS"
    && isEditableOutputEmail(currentEmail)
    && !isEmailAiMode;
  const showDocumentSuggestionsButton = userSuggestionsEnabled
    && activeTab === "DOCUMENTS"
    && !!currentOutput
    && canReview
    && !isAnyAiMode;
  const aiDocumentOutput = documentAiMode
    ? outputs.find(output => output.id === documentAiMode.outputId)
    : undefined;

  function isSendableEmail(email: OutputEmail | undefined): boolean {
    return !!email
      && !canReview
      && props.automationItem?.status === "COMPLETED"
      && !isOutputEmailSent(email, sentEmailIds)
      && String(email.sender?.id ?? "") === String(currentUserId ?? "");
  }

  const canSendEmail = activeTab === "EMAILS" && isSendableEmail(currentEmail);
  const hasMoreSendableEmails = outputEmails.some(isSendableEmail);

  const approvalRequests = props.automationItem?.approvalRequests;
  const initiatingUser = props.automationItem?.initiatingUser;

  const soloReview = useMemo(
    () => isSoloReview(approvalRequests, initiatingUser),
    [approvalRequests, initiatingUser],
  );

  const hasSignatureRequests = workflowHasSignatureRequests(workflow, props.automationItem);
  const activeSignatureRequests = useMemo(
    () => getActiveSignatureRequests(props.automationItem?.signatureRequests),
    [props.automationItem?.signatureRequests],
  );
  const showSignaturePendingNote = isReviewPending && hasSignatureRequests;
  const showActiveSignatureRequests = !isReviewPending && activeSignatureRequests.length > 0;
  const hasPendingActiveSignatureRequests = showActiveSignatureRequests && activeSignatureRequests.some((request) =>
    request.status !== "COMPLETED"
    && request.status !== "CANCELED"
    && request.status !== "EXPIRED"
    && request.status !== "ERROR"
    && request.status !== "LINK_UNPUBLISHED"
  );
  const showSignatureSection = showSignaturePendingNote || showActiveSignatureRequests;
  const signatureManualSend = isSignatureManualSendEnabled(workflow);
  const currentSignature = activeSignatureRequests[currentSignatureIndex];

  const automationItemId = props.automationItem?.id;
  const seenStorageKey = automationItemId ? `portant:seen-items:${automationItemId}` : null;

  const [seenItems, setSeenItems] = useState<Set<string>>(() => {
    if (!seenStorageKey || typeof window === "undefined") return new Set<string>();
    try {
      const stored = window.localStorage.getItem(seenStorageKey);
      if (!stored) return new Set<string>();
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed))
        return new Set<string>(parsed.filter((v): v is string => typeof v === "string"));
    } catch { /* ignore */ }
    return new Set<string>();
  });

  useEffect(() => {
    if (!seenStorageKey || typeof window === "undefined") {
      setSeenItems(new Set<string>());
      return;
    }
    try {
      const stored = window.localStorage.getItem(seenStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setSeenItems(new Set<string>(parsed.filter((v): v is string => typeof v === "string")));
          return;
        }
      }
    } catch { /* ignore */ }
    setSeenItems(new Set<string>());
  }, [seenStorageKey]);

  useEffect(() => {
    if (!seenStorageKey || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(seenStorageKey, JSON.stringify(Array.from(seenItems)));
    } catch { /* ignore quota errors */ }
  }, [seenStorageKey, seenItems]);

  const markSeen = (id: string) => {
    setSeenItems(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (activeTab === "DOCUMENTS" && currentOutput?.id)
      markSeen(currentOutput.id);
    if (activeTab === "EMAILS" && currentEmail?.id)
      markSeen(currentEmail.id);
    if (activeTab === "SIGNATURES" && currentSignature?.id)
      markSeen(currentSignature.id);
  }, [activeTab, currentOutput?.id, currentEmail?.id, currentSignature?.id]);

  const emailAutoSendGroups = useMemo(
    () => groupOutputEmailsByAutoSend(outputEmails),
    [outputEmails],
  );

  const manualSendNote = useMemo(
    () => getManualSendNote(
      props.automationItem?.stage,
      signatureManualSend,
      outputEmails.some(e => !isOutputEmailAutoSend(e)),
    ),
    [props.automationItem?.stage, signatureManualSend, outputEmails],
  );

  const primaryCtaKey = useMemo(
    () => getPrimaryCtaKey(outputs, outputEmails),
    [outputs, outputEmails],
  );

  const relevantUnseen = useMemo(
    () => getRelevantUnseen(
      outputs,
      outputEmails.filter(email => !isOutputEmailSent(email, sentEmailIds)),
      seenItems,
    ),
    [outputs, outputEmails, sentEmailIds, seenItems],
  );

  const unseenConfirmKey = useMemo(
    () => getUnseenConfirmKey(relevantUnseen),
    [relevantUnseen],
  );

  const showUnseenFooterWarning = canReview && !showRejectPanel && !showApproveConfirm && !!unseenConfirmKey;

  useEffect(() => {
    if (previewUrl)
      setShowPDFVersion(true);
  }, [previewUrl]);

  useEffect(() => {
    if (activeTab === "EMAILS" && outputEmails.length === 0)
      setActiveTab("DOCUMENTS");
  }, [activeTab, outputEmails.length]);

  useEffect(() => {
    if (activeTab === "SIGNATURES" && activeSignatureRequests.length === 0)
      setActiveTab("DOCUMENTS");
  }, [activeTab, activeSignatureRequests.length]);

  useEffect(() => {
    if (currentSignatureIndex >= activeSignatureRequests.length && activeSignatureRequests.length > 0)
      setCurrentSignatureIndex(0);
  }, [currentSignatureIndex, activeSignatureRequests.length]);

  useEffect(() => {
    if (reviewState === "REJECTED")
      setShowRejectPanel(false);
  }, [reviewState]);

  useEffect(() => {
    if (showRejectPanel)
      setShowApproveConfirm(false);
  }, [showRejectPanel]);

  const signatureCount = showActiveSignatureRequests ? activeSignatureRequests.length : 0;
  const totalItems = outputs.length + signatureCount + outputEmails.length;

  const currentLinearIndex = activeTab === "DOCUMENTS"
    ? currentOutputIndex
    : activeTab === "SIGNATURES"
      ? outputs.length + currentSignatureIndex
      : outputs.length + signatureCount + currentEmailIndex;

  const isAtFirstItem = currentLinearIndex <= 0;
  const isAtLastItem = currentLinearIndex >= totalItems - 1;

  function selectDocument(index: number) {
    if (isAnyAiMode) return;
    const output = outputs[index];
    if (!output) return;
    setActiveTab("DOCUMENTS");
    setCurrentOutputIndex(index);
    markSeen(output.id);
  }

  function selectSignature(index: number) {
    if (isAnyAiMode) return;
    const request = activeSignatureRequests[index];
    if (!request) return;
    setActiveTab("SIGNATURES");
    setCurrentSignatureIndex(index);
    markSeen(request.id);
  }

  function selectEmail(index: number) {
    if (isAnyAiMode) return;
    const email = outputEmails[index];
    if (!email) return;
    setActiveTab("EMAILS");
    setCurrentEmailIndex(index);
    markSeen(email.id);
  }

  function goPrevious() {
    if (isAnyAiMode || isAtFirstItem) return;
    const target = currentLinearIndex - 1;
    if (target < outputs.length) selectDocument(target);
    else if (target < outputs.length + signatureCount) selectSignature(target - outputs.length);
    else selectEmail(target - outputs.length - signatureCount);
  }

  function goNext() {
    if (isAnyAiMode || isAtLastItem) return;
    const target = currentLinearIndex + 1;
    if (target < outputs.length) selectDocument(target);
    else if (target < outputs.length + signatureCount) selectSignature(target - outputs.length);
    else selectEmail(target - outputs.length - signatureCount);
  }

  async function approveAutomationItem() {
    setShowApproveConfirm(false);
    setReviewState("APPROVING");
    try {
      const pendingSaves: Array<Promise<void>> = [];
      Array.from(emailEditorRefs.current.values()).forEach(handle => {
        if (handle && handle.isDirty())
          pendingSaves.push(handle.flushSave());
      });
      if (pendingSaves.length > 0)
        await Promise.all(pendingSaves);
      await postData(`/hubspot/app/automation-items/${props.automationItem?.id}/`, token);
      setReviewState("APPROVED");
    } catch (error) {
      console.error(error);
      setReviewState("PENDING");
    }
  }

  function closeModal(outcome: "DONE" | "CANCEL" = "DONE") {
    closeHubSpotIframeModal(outcome);
  }

  function handleApproveClick() {
    const unseen = getRelevantUnseen(outputs, outputEmails, seenItems);
    if (hasRelevantUnseen(unseen)) {
      setShowApproveConfirm(true);
      return;
    }
    void approveAutomationItem();
  }

  function rejectAutomationItem() {
    setReviewState("REJECTING");
    postData(`/hubspot/app/automation-items/${props.automationItem?.id}/reject/`, token, {
      requestChangesMessage: rejectMessage,
    })
      .then(() => { setReviewState("REJECTED"); setShowRejectPanel(false); })
      .catch((error) => { console.error(error); setReviewState("PENDING"); });
  }

  async function sendOutputEmail() {
    if (!currentEmail) return;
    setReviewState("SENDING");
    try {
      const handle = emailEditorRefs.current.get(currentEmail.id);
      if (handle?.isDirty())
        await handle.flushSave();
      await postData(`/hubspot/app/output-emails/${currentEmail.id}/send/`, token);
      setSentEmailIds(prev => new Set(prev).add(currentEmail.id));
      setReviewState("SENT");
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err));
      setReviewState("PENDING");
    }
  }

  function reviewNextPendingEmail() {
    const next = outputEmails.find(isSendableEmail);
    if (!next) return;
    const index = outputEmails.findIndex(e => e.id === next.id);
    if (index >= 0)
      setCurrentEmailIndex(index);
    setActiveTab("EMAILS");
    setReviewState("PENDING");
  }

  async function handlePersonalizeWithAi() {
    if (!currentEmail || isEmailAiMode) return;

    const editorHandle = emailEditorRefs.current.get(currentEmail.id);
    if (editorHandle?.isDirty())
      await editorHandle.flushSave();

    const pending = currentEmail.suggestions?.find(s => s.status === "PENDING");
    if (pending) {
      setEmailAiPersonalization({ emailId: currentEmail.id, phase: "ready", suggestion: pending });
      return;
    }

    setEmailAiPersonalization({ emailId: currentEmail.id, phase: "generating" });
    try {
      const updated: OutputEmail = await postData(`/hubspot/app/output-emails/${currentEmail.id}/suggestions/`, token);
      setOutputEmails(prev => prev.map(e => e.id === updated.id ? updated : e));
      const suggestion = updated.suggestions?.find(s => s.status === "PENDING");
      if (!suggestion) {
        showToast(t("No suggestion was generated."));
        setEmailAiPersonalization(null);
        return;
      }
      setEmailAiPersonalization({ emailId: currentEmail.id, phase: "ready", suggestion });
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err));
      setEmailAiPersonalization(null);
    }
  }

  async function handlePersonalizeDocumentWithAi(output: WorkflowOutput) {
    if (isAnyAiMode) return;

    const existingSuggestions = output.suggestions || [];
    if (existingSuggestions.length > 0) {
      setDocumentAiMode({ outputId: output.id, phase: "ready" });
      return;
    }

    if (documentSuggestionInFlightOutputIdsRef.current.has(output.id)) {
      setDocumentAiMode({ outputId: output.id, phase: "generating" });
      return;
    }

    documentSuggestionInFlightOutputIdsRef.current.add(output.id);
    setDocumentAiMode({ outputId: output.id, phase: "generating" });
    try {
      const updatedOutput: WorkflowOutput = await postData(`/hubspot/app/outputs/${output.id}/suggestions/`, token);
      handleSuggestionsChange(updatedOutput.id, updatedOutput.suggestions || []);
      setDocumentAiMode({ outputId: output.id, phase: "ready" });
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err));
      setDocumentAiMode(null);
    } finally {
      documentSuggestionInFlightOutputIdsRef.current.delete(output.id);
    }
  }

  async function handleApplyEmailSuggestion() {
    const { emailId, suggestion } = emailAiPersonalization ?? {};
    if (!emailId || !suggestion || emailAiPersonalization?.phase === "actioning") return;

    setEmailAiPersonalization(prev => prev ? { ...prev, phase: "actioning" } : null);
    try {
      const updated: OutputEmailSuggestion = await postData(
        `/hubspot/app/output-email-suggestions/${suggestion.id}/apply/`,
        token,
      );
      emailEditorRefs.current.get(emailId)?.applySuggestion(suggestion.proposedSubject, suggestion.proposedContent);
      setOutputEmails(prev => prev.map(e => e.id === emailId
        ? {
          ...e,
          emailSubject: suggestion.proposedSubject,
          emailContent: suggestion.proposedContent,
          suggestions: (e.suggestions || []).map(s => s.id === updated.id ? updated : s),
        }
        : e,
      ));
      setEmailAiPersonalization(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err));
      setEmailAiPersonalization(prev => prev ? { ...prev, phase: "ready" } : null);
    }
  }

  async function handleRevertEmailSuggestion() {
    const { emailId, suggestion, phase } = emailAiPersonalization ?? {};
    if (!emailId || emailAiPersonalization?.phase === "actioning") return;

    if (phase === "generating") {
      setEmailAiPersonalization(null);
      return;
    }

    if (!suggestion) return;

    setEmailAiPersonalization(prev => prev ? { ...prev, phase: "actioning" } : null);
    try {
      const updated: OutputEmailSuggestion = await postData(
        `/hubspot/app/output-email-suggestions/${suggestion.id}/dismiss/`,
        token,
      );
      setOutputEmails(prev => prev.map(e => e.id === emailId
        ? { ...e, suggestions: (e.suggestions || []).map(s => s.id === updated.id ? updated : s) }
        : e,
      ));
      setEmailAiPersonalization(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err));
      setEmailAiPersonalization(prev => prev ? { ...prev, phase: "ready" } : null);
    }
  }

  if (!props.automationItem)
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className='w-32 h-32' />
        <div className="text-gray-400 font-semibold text-center text-sm mt-2">{t("Loading")}...</div>
      </div>
    );

  if (reviewState === "APPROVED" || reviewState === "SENT") {
    const successTitle = reviewState === "SENT"
      ? getSuccessTitleKey("send_email")
      : getSuccessTitleKey(primaryCtaKey, getDocumentCount(outputs));

    return (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center gap-4">
        <CheckCircleIcon className="w-16 h-16 text-green" />
        <h2 className="text-2xl font-gilroy font-semibold text-black">
          {t(successTitle)}
        </h2>
        {reviewState === "APPROVED" &&
          <p className="text-sm text-gray-500 font-gilroy max-w-md text-balance">
            {t(getSuccessBodyKey(primaryCtaKey, hasSignatureRequests))}
          </p>
        }
        {reviewState === "SENT" && hasMoreSendableEmails ? (
          <div className="flex items-center gap-3 mt-2 w-full max-w-md">
            <button
              className="flex-1 h-10 bg-white hover:bg-gray-50 text-gray-700 font-gilroy font-semibold text-sm rounded-md border border-gray-200 transition-colors duration-150"
              onClick={() => closeModal("DONE")}
            >
              {t("Close")}
            </button>
            <button
              className="flex-1 h-10 bg-blue hover:opacity-90 text-white font-gilroy font-semibold text-sm rounded-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-opacity duration-150"
              onClick={reviewNextPendingEmail}
            >
              {t("Review next email")}
            </button>
          </div>
        ) : (
          <button
            className="mt-2 h-10 px-6 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 font-gilroy font-semibold text-sm rounded-md transition-colors"
            onClick={() => closeModal("DONE")}
          >
            {t("Close")}
          </button>
        )}
      </div>
    );
  }

  const showFooter = (canReview || canSendEmail) && !isEmailAiMode;
  const showApprovalSection = !soloReview;
  const approvalRequestsList = approvalRequests ?? [];
  const aggregateApprovalStatus: ApprovalDisplayStatus =
    approvalRequestsList.some(r => r.status === "REJECTED") ? "REJECTED"
      : approvalRequestsList.every(r => r.status === "APPROVED") ? "APPROVED"
        : "PENDING";

  return (
    <div className="flex h-full gap-3">
      <div className="flex-1 min-w-0 flex flex-col bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 h-9 shrink-0 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            {activeTab === "DOCUMENTS" && currentOutput &&
              <>
                {showPDFVersion &&
                  <a className="flex items-center gap-2 min-w-0 text-sm font-semibold font-gilroy text-gray-700 hover:text-blue hover:underline" href={hubspotFile?.fileUrl} target="_blank" rel="noreferrer">
                    <img className="h-[18px] w-[18px] rounded-sm shrink-0" src={MIMETYPE_ICON_MAP["application/pdf"]} alt="PDF File Icon" />
                    <TruncateTooltip label={hubspotFile?.fileName ?? currentOutput.outputName} className="flex-1" />
                    {hubspotFile?.fileUrl && <ExternalLinkIcon className="h-4 w-4 shrink-0" />}
                  </a>
                }
                {!showPDFVersion && microsoftFile &&
                  <a className="flex items-center gap-2 min-w-0 text-sm font-semibold font-gilroy text-gray-700 hover:text-blue hover:underline" href={microsoftFile.fileUrl} target="_blank" rel="noreferrer">
                    <img className="h-[18px] w-[18px] rounded-sm shrink-0" src={MIMETYPE_ICON_MAP[microsoftFile.fileMimeType!]} alt="Microsoft File Icon" />
                    <TruncateTooltip label={microsoftFile.fileName} className="flex-1" />
                    {microsoftFile.fileUrl && <ExternalLinkIcon className="h-4 w-4 shrink-0" />}
                  </a>
                }
                {!showPDFVersion && !microsoftFile && googleFile &&
                  <a className="flex items-center gap-2 min-w-0 text-sm font-semibold font-gilroy text-gray-700 hover:text-blue hover:underline" href={googleFile.url} target="_blank" rel="noreferrer">
                    <img className="h-[18px] w-[18px] rounded-sm shrink-0" src={MIMETYPE_ICON_MAP[googleFile.mimeType!]} alt="Google File Icon" />
                    <TruncateTooltip label={googleFile.name} className="flex-1" />
                    {googleFile.url && <ExternalLinkIcon className="h-4 w-4 shrink-0" />}
                  </a>
                }
              </>
            }
            {activeTab === "EMAILS" && currentEmail &&
              <div className="flex items-center gap-2 min-w-0 text-sm font-semibold font-gilroy text-gray-700">
                <MailIcon className="h-[18px] w-[18px] shrink-0" />
                <h3 className="truncate">{currentEmail.emailSubject || getRecipientSummary(currentEmail, t)}</h3>
              </div>
            }
            {activeTab === "SIGNATURES" && currentSignature &&
              <div className="flex items-center gap-2 min-w-0 text-sm font-semibold font-gilroy text-gray-700">
                <img className="h-[18px] w-[18px] shrink-0" src={BLOCK_ICON_MAP.SIGNATURE_REQUEST} alt="" />
                <h3 className="truncate">{currentSignature.emailSubject || getSignatureRequestRecipientLabel(currentSignature)}</h3>
              </div>
            }
          </div>

          <div className="flex items-center gap-4 shrink-0">
            {activeTab === "DOCUMENTS" && showPdfToggle &&
              <button
                className="flex items-center gap-1 text-xs font-semibold font-gilroy text-gray-500 hover:text-gray-700"
                onClick={() => setShowPDFVersion(!showPDFVersion)}
              >
                <img className="h-4 w-4 rounded" src={MIMETYPE_ICON_MAP[showPDFVersion ? (currentOutput.onedriveFile?.fileMimeType ?? googleFile?.mimeType ?? "application/pdf") : "application/pdf"]} alt="File icon" />
                {showPDFVersion ? t("View File") : t("View PDF")}
              </button>
            }
            <div className="flex items-center gap-4">
              <div className="text-xs text-gray-500 font-gilroy tabular-nums">
                {activeTab === "DOCUMENTS"
                  ? <>{t("Document")} {currentOutputIndex + 1} {t("of")} {outputs.length || 1}</>
                  : activeTab === "SIGNATURES"
                    ? <>{t("Signature Request")} {currentSignatureIndex + 1} {t("of")} {activeSignatureRequests.length || 1}</>
                    : <>{t("Email")} {currentEmailIndex + 1} {t("of")} {outputEmails.length || 1}</>
                }
              </div>
              <div className="flex items-center gap-1">
                <ChevronUpIcon
                  className={`w-4 h-4 ${isAnyAiMode || isAtFirstItem ? "text-gray-200 cursor-not-allowed" : "text-gray-400 hover:text-gray-600 cursor-pointer"}`}
                  onClick={isAnyAiMode || isAtFirstItem ? undefined : goPrevious}
                />
                <ChevronDownIcon
                  className={`w-4 h-4 ${isAnyAiMode || isAtLastItem ? "text-gray-200 cursor-not-allowed" : "text-gray-400 hover:text-gray-600 cursor-pointer"}`}
                  onClick={isAnyAiMode || isAtLastItem ? undefined : goNext}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 relative">
          <div className={`absolute inset-1.5 ${activeTab === "DOCUMENTS" ? "block" : "hidden"}`}>
            {showPDFVersion && previewUrl &&
              <div key="pdf" className="flex flex-col w-full h-full rounded overflow-hidden border border-gray-200 bg-white">
                <PDFViewer pdfUrl={previewUrl} />
              </div>
            }
            {!showPDFVersion && microsoftFile?.fileEmbedViewUrl &&
              <iframe key="ms-iframe" title={t("Microsoft Editor")} className="w-full h-full border border-gray-200 rounded overflow-hidden bg-white" src={microsoftFile.fileEmbedViewUrl} />
            }
            {!showPDFVersion && !microsoftFile?.fileEmbedViewUrl && googleEmbedUrl &&
              <iframe key="g-iframe" title={t("Google Editor")} className="w-full h-full border border-gray-200 rounded overflow-hidden bg-white" src={googleEmbedUrl} />
            }
          </div>

          {outputEmails.map(email => (
            <div
              key={email.id}
              className={`absolute inset-1.5 ${activeTab === "EMAILS" && email.id === currentEmail?.id ? "block" : "hidden"}`}
            >
              <OutputEmailEditor
                ref={(handle) => {
                  if (handle) emailEditorRefs.current.set(email.id, handle);
                  else emailEditorRefs.current.delete(email.id);
                }}
                token={token}
                email={email}
                outputs={outputs}
                readOnly={!isEditableOutputEmail(email)}
                suggestionPreview={
                  isEmailAiMode && aiEmailId === email.id && emailAiPersonalization
                    ? { phase: emailAiPersonalization.phase, suggestion: emailAiPersonalization.suggestion }
                    : undefined
                }
              />
            </div>
          ))}

          {activeSignatureRequests.map((request) => (
            <div
              key={request.id}
              className={`absolute inset-1.5 ${activeTab === "SIGNATURES" && request.id === currentSignature?.id ? "block" : "hidden"}`}
            >
              <SignatureRequestEmailPreview
                request={request}
                customFormatting={!!workflow?.signatureConfig?.customFormatting}
                emailButtonText={workflow?.signatureConfig?.emailButtonText}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="w-[360px] shrink-0 relative flex flex-col h-full pl-4">
        {isEmailAiMode && emailAiPersonalization && aiEmail ? (
          <AiSuggestionSidebar
            phase={emailAiPersonalization.phase}
            rationale={emailAiPersonalization.suggestion?.rationale}
            onBack={() => setEmailAiPersonalization(null)}
            onApply={() => { void handleApplyEmailSuggestion(); }}
            onRevert={() => { void handleRevertEmailSuggestion(); }}
          />
        ) : isDocumentAiMode && aiDocumentOutput ? (
          <DocumentSuggestionsSidebar
            token={token}
            outputId={aiDocumentOutput.id}
            outputName={getOutputDisplayName(aiDocumentOutput)}
            suggestions={aiDocumentOutput.suggestions || []}
            readOnly={!canReview}
            isGenerating={documentAiMode?.phase === "generating"}
            onSuggestionsChange={(suggestions) => handleSuggestionsChange(aiDocumentOutput.id, suggestions)}
            onNavigate={(key) => {
              setNavigation((prev) => {
                if (prev && prev.outputId === aiDocumentOutput.id && prev.key === key)
                  return prev;
                return { outputId: aiDocumentOutput.id, key };
              });
            }}
            onClose={() => setDocumentAiMode(null)}
          />
        ) : (
          <>
            <div className="flex flex-col pt-2 pb-6 shrink-0 border-b border-gray-200">
              <Breadcrumbs workflow={workflow} currentPage="PREVIEW" automationItem={props.automationItem} />
              <div className="mt-4 text-2xl font-gilroy font-semibold text-black leading-tight break-words text-balance">
                {workflow?.name}
              </div>
            </div>

            {showRejectPanel &&
              <div className={`relative z-0 flex-1 min-h-0 flex flex-col gap-2 pt-4 ${showFooter ? "pb-24" : "pb-4"}`}>
                <label className="text-sm font-gilroy font-semibold text-black">
                  {t("Message to the submitter (optional)")}
                </label>
                <textarea
                  autoFocus
                  className="flex-1 min-h-0 w-full text-sm font-gilroy border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:border-blue text-gray-700 resize-none"
                  value={rejectMessage}
                  onChange={e => setRejectMessage(e.target.value)}
                  placeholder={t("Let them know what needs to change...")}
                  disabled={reviewState === "REJECTING"}
                />
              </div>
            }

            {!showRejectPanel &&
              <>
                {showApproveConfirm && unseenConfirmKey &&
                  <div className={`relative z-0 flex-1 min-h-0 flex flex-col gap-3 pt-4 ${showFooter ? "pb-24" : "pb-4"}`}>
                    <p className="text-sm font-gilroy text-gray-700 text-balance">
                      {t(getUnseenConfirmMessage(unseenConfirmKey, outputEmails.length))}
                    </p>
                  </div>
                }
                {!(showApproveConfirm && unseenConfirmKey) &&
                  <div className={`relative z-0 flex-1 overflow-y-auto scrollbar-hidden pt-4 flex flex-col gap-5 -ml-2 pl-2 pr-0.5 ${showFooter ? "pb-24" : "pb-4"}`}>
                    {outputs.length > 0 &&
                      <div className="flex flex-col gap-2">
                        <div className="text-sm font-gilroy font-semibold text-black">{t("Documents")}</div>
                        <div className="flex flex-col gap-2">
                          {outputs.map((output, i) => {
                            const isSelected = activeTab === "DOCUMENTS" && currentOutputIndex === i;
                            const isUnseen = !seenItems.has(output.id);
                            const displayName = getOutputDisplayName(output);
                            return (
                              <button
                                key={output.id}
                                className={`flex items-center gap-3 h-9 px-3 rounded-md border bg-white text-left transition-colors ${isSelected ? "border-[#C9DDFF] card-selected-ring" : "border-gray-200 hover:border-gray-300"}`}
                                onClick={() => selectDocument(i)}
                              >
                                <img className="h-4 w-4 shrink-0 rounded-sm" src={getOutputFileIcon(output)} alt="" />
                                <TruncateTooltip label={displayName} className="flex-1 text-sm font-gilroy text-gray-700" />
                                {isUnseen &&
                                  <div className="w-2 h-2 rounded-full bg-green shrink-0" />
                                }
                              </button>
                            );
                          })}
                        </div>
                        {showDocumentSuggestionsButton &&
                          <button
                            type="button"
                            onClick={() => { void handlePersonalizeDocumentWithAi(currentOutput); }}
                            className="w-fit self-center h-9 px-4 inline-flex items-center justify-center text-sm font-semibold font-gilroy rounded-full portant-ai-gradient-border portant-ai-full-fill-7 transition-colors"
                          >
                            <span className="inline-flex items-center justify-center gap-1.5">
                              <span className="portant-ai-gradient-text w-fit">{t("Personalize with AI")}</span>
                              <SparklesIcon className="w-4 h-4 shrink-0 text-[#0038C6]" />
                              <BetaLabel inline />
                            </span>
                          </button>
                        }
                      </div>
                    }

                    {showApprovalSection &&
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-gilroy font-semibold text-black">{t("Document Approval Status")}</div>
                          <ApproverStatusPill
                            status={aggregateApprovalStatus}
                            label={aggregateApprovalStatus === "PENDING" ? t("In Progress") : undefined}
                            t={t}
                          />
                        </div>
                        <div className="flex flex-col gap-2 px-3 py-2.5 rounded-md border border-gray-200 bg-white">
                          {approvalRequestsList.map((request) => (
                            <div key={request.id} className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <Avatar approver={{
                                  id: request.approver.id,
                                  name: request.approver.fullName,
                                  avatarUrl: request.approver.avatar,
                                }} />
                                <div className="min-w-0 text-sm font-gilroy text-gray-700 truncate">{request.approver.fullName}</div>
                              </div>
                              <ApproverStatusPill status={mapApproverRowStatus(request.status)} t={t} />
                            </div>
                          ))}
                        </div>
                      </div>
                    }

                    {showSignatureSection &&
                      <>
                        <div className="border-t border-gray-200" />
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-gilroy font-semibold text-black">{t("Signature Requests")}</div>
                            <div className="flex items-center gap-2">
                              {hasPendingActiveSignatureRequests &&
                                <div className="text-xs font-gilroy font-semibold px-2 py-0.5 rounded-full bg-blue/10 text-blue">
                                  {t("In Progress")}
                                </div>
                              }
                              {showActiveSignatureRequests && signatureManualSend &&
                                <div className="text-xs font-gilroy font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                  {t("Auto-Send")}: {t("Off")}
                                </div>
                              }
                            </div>
                          </div>
                          {showSignaturePendingNote &&
                            <p className="text-xs text-gray-500 font-gilroy text-balance">
                              {t("Signature Requests will be generated after document approvals.")}
                            </p>
                          }
                          {showActiveSignatureRequests &&
                            <>
                              <div className="flex flex-col gap-2">
                                {activeSignatureRequests.map((request: SignatureRequest, i) => {
                                  const isSelected = activeTab === "SIGNATURES" && currentSignatureIndex === i;
                                  const isUnseen = !seenItems.has(request.id) && request.status === "PENDING";
                                  return (
                                    <button
                                      key={request.id}
                                      type="button"
                                      className={`flex items-center gap-3 h-10 px-3 rounded-md border bg-white text-left transition-colors ${isSelected ? "border-[#C9DDFF] card-selected-ring" : "border-gray-200 hover:border-gray-300"}`}
                                      onClick={() => selectSignature(i)}
                                    >
                                      <img className="h-4 w-4 shrink-0" src={BLOCK_ICON_MAP.SIGNATURE_REQUEST} alt="" />
                                      <div className="flex-1 min-w-0 text-sm font-gilroy text-gray-700 truncate">
                                        {getSignatureRequestRecipientLabel(request)}
                                      </div>
                                      <SignatureRequestStatusIndicator
                                        status={request.status}
                                        enableDraftRequests={signatureManualSend}
                                        t={t}
                                      />
                                      {isUnseen &&
                                        <div className="w-2 h-2 rounded-full bg-green shrink-0" />
                                      }
                                    </button>
                                  );
                                })}
                              </div>
                              {manualSendNote === "signature" && automationItemId &&
                                <p className="text-xs text-gray-500 font-gilroy">
                                  {t("Signature requests require manual sending.")}{" "}
                                  <a
                                    className="text-blue hover:underline"
                                    href={`https://app.portant.co/outputs/${automationItemId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {t("Open in Portant.")}
                                  </a>
                                </p>
                              }
                            </>
                          }
                        </div>
                      </>
                    }

                    {outputEmails.length > 0 &&
                      <>
                        <div className="border-t border-gray-200" />
                        <div className="flex flex-col gap-5">
                          {emailAutoSendGroups.map((group) => {
                            const groupHasUnsent = group.emails.some((email) => !isOutputEmailSent(email, sentEmailIds));
                            return (
                              <div key={group.autoSend ? "auto-send-on" : "auto-send-off"} className="flex flex-col gap-2">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-sm font-gilroy font-semibold text-black">{t("Email")}</div>
                                  {groupHasUnsent &&
                                    <div className={`text-xs font-gilroy font-semibold px-2 py-0.5 rounded-full ${group.autoSend ? "bg-green/10 text-green" : "bg-gray-100 text-gray-500"
                                      }`}>
                                      {t("Auto-Send")}: {group.autoSend ? t("On") : t("Off")}
                                    </div>
                                  }
                                </div>
                                <div className="flex flex-col gap-2">
                                  {group.emails.map((email) => {
                                    const emailIndex = outputEmails.findIndex(e => e.id === email.id);
                                    const isSelected = activeTab === "EMAILS" && currentEmailIndex === emailIndex;
                                    const isUnseen = !seenItems.has(email.id) && !isOutputEmailSent(email, sentEmailIds);
                                    const hasAttachments = (email.attachments?.length ?? 0) > 0;
                                    return (
                                      <button
                                        key={email.id}
                                        className={`flex items-center gap-3 h-10 px-3 rounded-md border bg-white text-left transition-colors ${isSelected ? "border-[#C9DDFF] card-selected-ring" : "border-gray-200 hover:border-gray-300"}`}
                                        onClick={() => selectEmail(emailIndex)}
                                      >
                                        <MailIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                        <div className="flex-1 min-w-0 text-sm font-gilroy text-gray-700 truncate">
                                          {getRecipientSummary(email, t)}
                                        </div>
                                        {hasAttachments ? (
                                          <PaperClipIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                        ) : (
                                          <div className="h-4 w-4 shrink-0" aria-hidden />
                                        )}
                                        <OutputEmailStatusIndicator
                                          email={email}
                                          sentEmailIds={sentEmailIds}
                                          t={t}
                                        />
                                        {isUnseen &&
                                          <div className="w-2 h-2 rounded-full bg-green shrink-0" />
                                        }
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                          {manualSendNote === "email" && automationItemId && !canSendEmail && !hasMoreSendableEmails &&
                            <p className="text-xs text-gray-500 font-gilroy">
                              {t("Email drafts require manual sending.")}{" "}
                              <a
                                className="text-blue hover:underline"
                                href={`https://app.portant.co/outputs/${automationItemId}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {t("Open in Portant.")}
                              </a>
                            </p>
                          }
                          {showPersonalizeButton &&
                            <button
                              type="button"
                              onClick={() => { void handlePersonalizeWithAi(); }}
                              className="w-fit self-center h-9 px-4 inline-flex items-center justify-center text-sm font-semibold font-gilroy rounded-full portant-ai-gradient-border portant-ai-full-fill-7 transition-colors"
                            >
                              <span className="inline-flex items-center justify-center gap-1.5">
                                <span className="portant-ai-gradient-text w-fit">{t("Personalize with AI")}</span>
                                <SparklesIcon className="w-4 h-4 shrink-0 text-[#0038C6]" />
                                <BetaLabel inline />
                              </span>
                            </button>
                          }
                        </div>
                      </>
                    }
                  </div>
                }
              </>
            }

            {showFooter &&
              <div className="absolute bottom-0 left-0 right-0 z-10 pl-4 pt-3 pb-3 bg-white border-t border-gray-200">
                <div className="absolute -top-6 left-0 right-0 z-10 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                {showUnseenFooterWarning && unseenConfirmKey &&
                  <p className="text-xs text-gray-500 font-gilroy mb-2 text-balance">
                    {t(getUnseenFooterWarning(unseenConfirmKey, outputEmails.length))}
                  </p>
                }
                <div className="flex items-center gap-3">
                  {showRejectPanel ? (
                    <>
                      <button
                        className="flex-1 h-10 bg-white hover:bg-gray-50 text-gray-700 font-gilroy font-semibold text-sm rounded-md border border-gray-200 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => setShowRejectPanel(false)}
                        disabled={reviewState === "REJECTING"}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        className="flex-1 h-10 bg-blue hover:opacity-90 text-white font-gilroy font-semibold text-sm rounded-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-opacity duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={rejectAutomationItem}
                        disabled={reviewState === "REJECTING"}
                      >
                        {reviewState === "REJECTING" ? `${t("Sending")}...` : t("Send")}
                      </button>
                    </>
                  ) : showApproveConfirm ? (
                    <>
                      <button
                        className="flex-1 h-10 bg-white hover:bg-gray-50 text-gray-700 font-gilroy font-semibold text-sm rounded-md border border-gray-200 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => setShowApproveConfirm(false)}
                        disabled={reviewState === "APPROVING"}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        className="flex-1 h-10 bg-blue hover:opacity-90 text-white font-gilroy font-semibold text-sm rounded-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-opacity duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => { void approveAutomationItem(); }}
                        disabled={reviewState === "APPROVING"}
                      >
                        {reviewState === "APPROVING" ? `${t("Approving")}...` : t("Proceed")}
                      </button>
                    </>
                  ) : canSendEmail ? (
                    <>
                      <button
                        className="flex-1 h-10 bg-white hover:bg-gray-50 text-gray-700 font-gilroy font-semibold text-sm rounded-md border border-gray-200 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => closeModal("DONE")}
                        disabled={reviewState === "SENDING"}
                      >
                        {t("Close")}
                      </button>
                      <button
                        className="flex-1 h-10 bg-blue hover:opacity-90 text-white font-gilroy font-semibold text-sm rounded-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-opacity duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => { void sendOutputEmail(); }}
                        disabled={reviewState === "SENDING"}
                      >
                        {reviewState === "SENDING" ? `${t("Sending")}...` : t("Send email")}
                      </button>
                    </>
                  ) : (
                    <>
                      {(reviewState === "PENDING" || reviewState === "REJECTING") &&
                        <button
                          className="flex-1 h-10 bg-white hover:bg-gray-50 text-gray-700 font-gilroy font-semibold text-sm rounded-md border border-gray-200 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => soloReview ? closeModal("CANCEL") : setShowRejectPanel(true)}
                          disabled={reviewState === "REJECTING"}
                        >
                          {soloReview ? t("Close") : t("Request Changes")}
                        </button>
                      }
                      {reviewState === "REJECTED" &&
                        <button className="flex-1 h-10 bg-gray-100 border border-gray-200 text-gray-400 font-gilroy font-semibold text-sm rounded-md cursor-not-allowed" disabled>
                          {t("Changes Requested")}
                        </button>
                      }
                      {reviewState === "PENDING" &&
                        <button
                          className="flex-1 h-10 bg-blue hover:opacity-90 text-white font-gilroy font-semibold text-sm rounded-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-opacity duration-150"
                          onClick={handleApproveClick}
                        >
                          {t(getPrimaryCtaLabel(primaryCtaKey, getDocumentCount(outputs)))}
                        </button>
                      }
                      {reviewState === "APPROVING" &&
                        <button className="flex-1 h-10 bg-white border border-blue text-blue font-gilroy font-semibold text-sm rounded-md" disabled>
                          {t("Approving")}...
                        </button>
                      }
                    </>
                  )}
                </div>
              </div>
            }
          </>
        )}
      </div>
    </div>
  );
}

export default AppWorkflowPreview;
